// @ts-nocheck
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id, Doc } from '@/convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
	Card,
	CardHeader,
	CardTitle,
	CardContent,
	CardFooter,
} from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { Profile } from '@/types/profiles';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useConfetti } from '@/hooks/useConfetti';
import { useAuth } from '@/hooks/useAuth';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { cn } from '@/lib/utils';
import { TribeCard3D } from '@/components/gamification/TribeCard3D';

// Define types for pods and members
interface PodMember {
	email: string;
	socialUsername: string;
	uploadPostProfileKey: string;
}

interface Pod {
	_id: Id<'engagementPods'>;
	platform: string;
	members: PodMember[];
}

interface User {
	_id: string;
	email?: string;
	name?: string;
}

function PlatformPill({ platform }: { platform: string }) {
	const colors = {
		twitter: 'bg-purple-300 text-purple-700',
		linkedin: 'bg-indigo-300 text-indigo-700',
		facebook: 'bg-blue-300 text-blue-700',
		tiktok: 'bg-gradient-to-r from-pink-500 to-cyan-500 text-white shadow-sm',
	};

	return (
		<span
			className={cn(
				'px-2 py-1 rounded-full text-xs font-semibold',
				colors[platform as keyof typeof colors]
			)}
		>
			{platform.toUpperCase()}
		</span>
	);
}

function DefaultGroupCard() {
	const { toast } = useToast();
	const { user, isSuperAdmin } = useAuth();
	const createDefaultGroup = useMutation(
		api.profileGroups.createDefaultGroup
	);
	const updateDefaultGroup = useMutation(
		api.profileGroups.updateDefaultGroup
	);
	const groups = useQuery(api.profileGroups.list, {
		userId: user?.id as Id<'users'> | undefined,
		isSuperAdmin: isSuperAdmin || false,
	});
	const profiles = useQuery(
		api.profiles.list,
		user?.id
			? {
					userId: user.id as any, // eslint-disable-line @typescript-eslint/no-explicit-any
					isSuperAdmin: isSuperAdmin || false,
				}
			: 'skip'
	) as Profile[];

	// Filter profiles based on user permissions
	const availableProfiles = React.useMemo(() => {
		if (!profiles || !user?.id) return [];
		if (isSuperAdmin) return profiles;
		// Regular admins only see profiles they created or with matching email
		return profiles.filter(
			(profile) =>
				(profile.userId as unknown as string) ===
					(user.id as unknown as string) ||
				profile.email.toLowerCase() === user?.email?.toLowerCase()
		);
	}, [profiles, user, isSuperAdmin]);

	const defaultGroup = groups?.find(
		(g: Doc<'profileGroups'>) =>
			g.isDefault &&
			(!g.createdBy ||
				(g.createdBy as unknown as string) ===
					(user?.id as unknown as string))
	);

	const [randomCount, setRandomCount] = useState(10);
	const migrateExistingProfiles = useMutation(
		api.profileGroups.migrateExistingProfilesToDefaultGroups
	);

	const handleCreateOrUpdate = async () => {
		try {
			if (!user?.id) {
				throw new Error('User not authenticated');
			}

			if (defaultGroup) {
				await updateDefaultGroup({
					groupId: defaultGroup._id,
					name: 'Random Profiles Group',
					description:
						'Automatically generated group with random profiles',
					randomCount,
				});
			} else {
				await createDefaultGroup({
					name: 'Random Profiles Group',
					description:
						'Automatically generated group with random profiles',
					randomCount,
					createdBy: user.id as any, // eslint-disable-line @typescript-eslint/no-explicit-any
				});
			}

			toast({
				title: '✨ Random Group Updated ✨',
				description: 'Random profile group updated successfully',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to update random group',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleMigrateExistingProfiles = async () => {
		try {
			// Show loading toast
			toast({
				title: 'Migration Started',
				description: 'Creating default groups for existing profiles...',
				duration: 3000,
			});

			// Run the migration
			const result = await migrateExistingProfiles();

			// Show success toast with results
			toast({
				title: 'Migration Complete',
				description: `Created ${result.created} groups, skipped ${result.skipped} profiles out of ${result.total} total.`,
				duration: 5000,
			});

			if (result.errors.length > 0) {
				console.error('Migration errors:', result.errors);
				toast({
					title: 'Migration had errors',
					description: `${result.errors.length} errors occurred. Check console for details.`,
					variant: 'destructive',
					duration: 5000,
				});
			}
		} catch (error) {
			toast({
				title: 'Migration Failed',
				description:
					error instanceof Error ? error.message : 'Unknown error',
				variant: 'destructive',
				duration: 5000,
			});
		}
	};

	return (
		<Card className='mb-6 mt-16'>
			<CardHeader>
				<CardTitle className='orange-title'>
					Random Profiles Group
				</CardTitle>
			</CardHeader>
			<CardContent className='space-y-4'>
				<div className='space-y-2'>
					<p className='text-sm text-muted-foreground'>
						{isSuperAdmin
							? 'This group automatically selects random profiles from your database.'
							: 'This group automatically selects random profiles from your available profiles.'}
					</p>
					<div className='flex items-center gap-4'>
						<Input
							type='number'
							min='1'
							max={availableProfiles.length}
							value={randomCount}
							onChange={(e) => {
								const value = parseInt(e.target.value);
								setRandomCount(
									Math.min(value, availableProfiles.length)
								);
							}}
							className='w-32'
						/>
						<span className='text-sm text-muted-foreground'>
							profiles to include (max: {availableProfiles.length}
							)
						</span>
					</div>
				</div>
				<Button
					onClick={handleCreateOrUpdate}
					className='w-full bg-purple-600 hover:bg-purple-700 text-white text-lg font-semibold'
					disabled={randomCount > availableProfiles.length}
				>
					{defaultGroup
						? '🔄 Refresh Random Profiles'
						: '✨ Create Random Group'}
				</Button>
			</CardContent>
			<CardFooter className='flex flex-col gap-4'>
				<div className='w-full mt-4 border-t pt-4'>
					<h3 className='text-md font-semibold mb-2'>Migrations</h3>
					<Button
						onClick={handleMigrateExistingProfiles}
						variant='outline'
						className='w-full'
					>
						Create Default Groups For Existing Profiles
					</Button>
					<p className='text-xs text-gray-500 mt-2'>
						This will scan all profiles without default groups and
						create them using the platform username.
					</p>
				</div>
			</CardFooter>
		</Card>
	);
}

export function ProfileGroups() {
	const { toast } = useToast();
	const { triggerConfetti, Confetti } = useConfetti();
	const { user, isAdmin, isSuperAdmin } = useAuth();
	const migrateExistingProfiles = useMutation(
		api.profileGroups.migrateExistingProfilesToDefaultGroups
	);

	// Add function to handle migration
	const handleMigrateProfiles = async () => {
		try {
			toast({
				title: 'Migration Started',
				description: 'Creating default groups for existing profiles...',
				duration: 3000,
			});

			const result = await migrateExistingProfiles();

			toast({
				title: 'Migration Complete',
				description: `Created ${result.created} groups, skipped ${result.skipped} profiles out of ${result.total} total.`,
				duration: 5000,
			});
		} catch (error) {
			toast({
				title: 'Migration Failed',
				description:
					error instanceof Error ? error.message : 'Unknown error',
				variant: 'destructive',
				duration: 5000,
			});
		}
	};

	const userPods = useQuery(api.engagementPods.list, {
		userId: user?.id ? (user.id as any) : null, // eslint-disable-line @typescript-eslint/no-explicit-any
		requestingUserId: user?.id ? (user.id as unknown as string) : 'skip',
	});

	const [newGroup, setNewGroup] = React.useState({
		name: '',
		description: '',
		profileIds: [] as Id<'profiles'>[],
		isCommunity: false,
	});
	const [searchTerm, setSearchTerm] = useState('');

	const profiles = useQuery(
		api.profiles.list,
		user?.id
			? {
					userId: user.id as any, // eslint-disable-line @typescript-eslint/no-explicit-any
					isSuperAdmin: isSuperAdmin || false,
				}
			: 'skip'
	) as Profile[];

	// Filter available profiles based on user permissions
	const availableProfiles = React.useMemo(() => {
		if (!profiles || !user?.id) return [];
		// Super admins see all profiles
		if (isSuperAdmin) return profiles;
		// Regular users see:
		// 1. Profiles they created (userId matches)
		// 2. Profiles explicitly shared with community
		return profiles.filter(
			(profile) =>
				(profile.userId as unknown as string) === (user.id as unknown as string) ||
				profile.isCommunity === true
		);
	}, [profiles, user, isSuperAdmin]);

	const allGroups = useQuery(api.profileGroups.list, {
		userId: user?.id as Id<'users'> | undefined,
		isSuperAdmin: isSuperAdmin || false,
	});
	const createGroup = useMutation(api.profileGroups.create);
	const updateGroup = useMutation(api.profileGroups.update);
	const removeGroup = useMutation(api.profileGroups.remove);
	const removeMember = useMutation(api.engagementPods.removeMember);
	const addMember = useMutation(api.engagementPods.addMember);
	const removePodProfile = useMutation(api.urls.removePodProfile);
	const addPodMember = useMutation(api.engagementPods.addMember);

	// Filter groups based on user permissions
	const groups = React.useMemo(() => {
		if (!allGroups) return undefined;
		if (isSuperAdmin) return allGroups; // Super admins see all groups
		if (isAdmin && user?.id) {
			// Regular admins see groups they created and their random groups
			return allGroups.filter(
				(group: Doc<'profileGroups'>) =>
					(group.createdBy as unknown as string) ===
						(user.id as unknown as string) ||
					(group.isDefault &&
						group.randomCount !== undefined &&
						(group.createdBy as unknown as string) ===
							(user.id as unknown as string))
			);
		}
		return []; // Non-admins see no groups
	}, [allGroups, isAdmin, isSuperAdmin, user]);

	const [editingGroup, setEditingGroup] = React.useState<{
		groupId: Id<'profileGroups'>;
		name: string;
		description: string;
		profileIds: Id<'profiles'>[];
		isCommunity?: boolean;
	} | null>(null);

	const [groupToDelete, setGroupToDelete] =
		useState<Id<'profileGroups'> | null>(null);
	const [memberToRemove, setMemberToRemove] = useState<{
		profileId: Id<'profiles'>;
		profileName: string;
		groupId: Id<'profileGroups'>;
	} | null>(null);

	const queryPods = useQuery(api.engagementPods.list, {
		userId: null,
		requestingUserId: user?.id ? (user.id as unknown as string) : 'skip',
	});

	const users = useQuery(api.users.list, {
		requestingUserId: user?.id ? (user.id as unknown as string) : '',
		apiKey: user?.initialApiKey ?? '',
	});

	const handleCreateGroup = async () => {
		try {
			if (!user?.id) {
				throw new Error('User not authenticated');
			}

			await createGroup({
				name: newGroup.name,
				description: newGroup.description,
				profileIds: newGroup.profileIds,
				createdBy: user.id as any, // eslint-disable-line @typescript-eslint/no-explicit-any
				isCommunity: newGroup.isCommunity,
			});

			setNewGroup({ name: '', description: '', profileIds: [], isCommunity: false });
			triggerConfetti();
			toast({
				title: '✨ Group Created ✨',
				description: 'Profile group created successfully',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to create group',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleStartEdit = (group: Doc<'profileGroups'>) => {
		setEditingGroup({
			groupId: group._id,
			name: group.name,
			description: group.description || '',
			profileIds: group.profileIds,
			isCommunity: group.isCommunity || false,
		});
		// Auto-expand the card when editing
		setExpandedGroups((prev) => ({
			...prev,
			[group._id]: true,
		}));
	};

	const handleSaveEdit = async () => {
		if (!editingGroup) return;

		try {
			// Find profiles that were removed from the group
			const originalGroup = groups?.find(
				(g: Doc<'profileGroups'>) => g._id === editingGroup.groupId
			);
			const removedProfileIds =
				originalGroup?.profileIds.filter(
					(id: Id<'profiles'>) =>
						!editingGroup.profileIds.includes(id)
				) || [];

			// Find profiles that were added to the group
			const addedProfileIds =
				editingGroup.profileIds.filter(
					(id: Id<'profiles'>) =>
						!originalGroup?.profileIds.includes(id)
				) || [];

			// Get the removed and added profiles
			const removedProfiles = removedProfileIds
				.map((id: Id<'profiles'>) =>
					profiles?.find((p) => p._id === id)
				)
				.filter(Boolean);

			const addedProfiles = addedProfileIds
				.map((id: Id<'profiles'>) =>
					profiles?.find((p) => p._id === id)
				)
				.filter(Boolean);

			// Update the group first
			await updateGroup({
				groupId: editingGroup.groupId as Id<'profileGroups'>,
				name: editingGroup.name,
				description: editingGroup.description,
				profileIds: editingGroup.profileIds,
				isCommunity: editingGroup.isCommunity,
			});

			// Get all pods
			const allPods = queryPods;
			if (!allPods) return;

			// Handle removed profiles
			for (const profile of removedProfiles) {
				if (!profile) continue;

				// Find pods where this profile is a member
				const relevantPods = allPods.filter((pod: Pod) =>
					pod.members.some(
						(member: PodMember) => member.email === profile.email
					)
				);

				// Remove profile from each pod
				for (const pod of relevantPods) {
					await handleRemoveMember(pod._id, profile.email);
				}
			}

			// Handle added profiles
			for (const profile of addedProfiles) {
				if (!profile) continue;

				// Find all pods that have any profile from this group
				const relevantPods = allPods.filter((pod: Pod) => {
					// Get all emails in the group (including newly added profiles)
					const groupEmails = editingGroup.profileIds
						.map(
							(id: Id<'profiles'>) =>
								profiles?.find((p) => p._id === id)?.email
						)
						.filter(Boolean);

					// Check if pod has any member from the group
					return pod.members.some((member: PodMember) =>
						groupEmails.includes(member.email)
					);
				});

				// Add profile to each relevant pod
				for (const pod of relevantPods) {
					// Skip if profile is already in the pod
					if (
						pod.members.some(
							(m: PodMember) => m.email === profile.email
						)
					)
						continue;

					// Check if profile is linked to the correct platform
					const isLinked = profile.linkStatuses?.some(
						(status) =>
							status.status === 'linked' &&
							status.platform.toLowerCase() ===
								pod.platform.toLowerCase()
					);

					if (!isLinked) continue;

					// Create the member object
					const member = {
						email: profile.email,
						socialUsername: profile.profileTitle,
						uploadPostProfileKey: profile.profileKey,
					};

					// Add the member to the pod
					await addMember({
						podId: pod._id,
						member,
					});
				}
			}

			setEditingGroup(null);
			toast({
				title: '✨ Group Updated ✨',
				description: 'Profile group updated successfully',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to update group',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleRemoveMember = async (
		podId: Id<'engagementPods'>,
		memberEmail: string
	) => {
		await removeMember({
			podId,
			memberEmail,
		});
	};

	const getUserInfo = (userId: string) => {
		const user = users?.find((u: User) => u._id === userId);
		return {
			email: user?.email || 'Unknown',
			name: user?.name || 'Unknown User',
		};
	};

	const [expandedGroups, setExpandedGroups] = useState<{
		[key: string]: boolean;
	}>({});

	return (
		<div className='container mx-auto p-6 pb-60'>
			<div className='flex justify-between items-center mb-6'>
				<div>
					<h1 className='text-3xl font-bold py-4'>Tribes</h1>
					<p className='text-lg text-gray-500 -mt-2 mb-4'>
						Create and manage social tribes
					</p>
				</div>
			</div>
			<Confetti />

			<Card className='mt-8'>
				<CardHeader className='mb-8'>
					<div className='flex justify-between items-center'>
						<CardTitle className='orange-title'>
							{isSuperAdmin ? 'All Groups' : 'My Tribes'}
						</CardTitle>
						<Input
							placeholder='Search groups...'
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className='max-w-md w-80'
						/>
					</div>
				</CardHeader>
				<CardContent>
					<div className='space-y-8'>
						{groups?.length === 0 ? (
							<p className='text-center text-gray-500 py-4'>
								No groups found. Create your first group below!
							</p>
						) : (
							groups
								?.filter(
									(group: Doc<'profileGroups'>) =>
										group.name
											.toLowerCase()
											.includes(
												searchTerm.toLowerCase()
											) ||
										group.description
											?.toLowerCase()
											.includes(searchTerm.toLowerCase())
								)
								?.map((group: Doc<'profileGroups'>) => (
									<div key={group._id} className="space-y-4">
										<TribeCard3D
											tribe={group}
											canEdit={isSuperAdmin || (group.createdBy as unknown as string) === (user?.id as unknown as string)}
											userId={user?.id}
											className="mb-6"
											enableTilt={true}
										/>
										<div className='space-y-4'>
											<div className='space-y-4'>
												<div className='flex items-center gap-2'>
													<motion.button
													onClick={() =>
														setExpandedGroups(
															(prev) => ({
																...prev,
																[group._id]:
																	!prev[
																		group
																			._id
																	],
															})
														)
													}
													className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
													animate={{
														rotate: expandedGroups[
															group._id
														]
															? 180
															: 0,
													}}
													transition={{
														duration: 0.2,
														ease: 'easeInOut',
													}}
												>
													{expandedGroups[group._id]
														? '▶️'
														: '▶️'}
												</motion.button>
												<h2 className='text-3xl font-bold text-pink-500 uppercase flex items-center gap-2'>
													{group.name}
													{group.isCommunity && (
														<span className='text-sm bg-green-100 text-green-700 px-2 py-1 rounded-full normal-case'>
															🌍 Community
														</span>
													)}
												</h2>
												<div className='flex gap-2 ml-auto'>
													{editingGroup?.groupId ===
													group._id ? (
														<>
															<Button
																variant='outline'
																size='sm'
																onClick={
																	handleSaveEdit
																}
																disabled={
																	!editingGroup?.name ||
																	editingGroup
																		?.profileIds
																		.length ===
																		0
																}
																className='bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 rounded-full'
															>
																<span className='inline-flex items-center gap-2'>
																	✨ Save
																</span>
															</Button>
															<Button
																variant='outline'
																size='sm'
																onClick={() =>
																	setEditingGroup(
																		null
																	)
																}
																className='bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 rounded-full'
															>
																❌ Cancel
															</Button>
														</>
													) : (
														<div className='flex gap-2'>
															<Button
																variant='outline'
																size='sm'
																onClick={() =>
																	handleStartEdit(
																		group
																	)
																}
																className='bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 rounded-full'
															>
																<span className='inline-flex items-center gap-2'>
																	✏️ Edit
																</span>
															</Button>
															<Button
																variant='outline'
																size='sm'
																onClick={() =>
																	setGroupToDelete(
																		group._id
																	)
																}
																className='bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 rounded-full'
															>
																<span className='inline-flex items-center gap-2'>
																	🗑️ Delete
																</span>
															</Button>
														</div>
													)}
												</div>
											</div>
											{group.description && (
												<p className='text-lg text-gray-400 pl-10'>
													{group.description}
												</p>
											)}
											<p className='text-lg text-gray-500 pl-10'>
												Profiles:{' '}
												{group.profileIds.length}
											</p>
										</div>
										<AnimatePresence>
											{expandedGroups[group._id] && (
												<motion.div
													className='pl-10 pr-4'
													initial={{
														opacity: 0,
														height: 0,
													}}
													animate={{
														opacity: 1,
														height: 'auto',
													}}
													exit={{
														opacity: 0,
														height: 0,
													}}
													transition={{
														duration: 0.2,
													}}
												>
													{editingGroup?.groupId ===
													group._id ? (
														<div className='w-full space-y-6 p-6 bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-xl border border-purple-500/20 mt-4'>
															<div className='space-y-2'>
																<Label className='text-sm font-semibold text-purple-300'>
																	Tribe Name
																</Label>
																<Input
																	value={
																		editingGroup?.name ||
																		''
																	}
																	onChange={(
																		e
																	) => {
																		if (
																			editingGroup
																		) {
																			setEditingGroup(
																				{
																					...editingGroup,
																					name: e
																						.target
																						.value,
																				}
																			);
																		}
																	}}
																	className='w-full bg-black/30 border-purple-500/30 focus:border-purple-400 text-white placeholder-gray-400 rounded-lg px-4 py-3 transition-all duration-200 hover:bg-black/40'
																	placeholder='Enter a memorable tribe name...'
																/>
															</div>
															
															<div className='space-y-2'>
																<Label className='text-sm font-semibold text-purple-300'>
																	Description
																</Label>
																<Textarea
																	value={
																		editingGroup?.description ||
																		''
																	}
																	onChange={(
																		e
																	) => {
																		if (
																			editingGroup
																		) {
																			setEditingGroup(
																				{
																					...editingGroup,
																					description:
																						e
																							.target
																							.value,
																				}
																			);
																		}
																	}}
																	className='w-full bg-black/30 border-purple-500/30 focus:border-purple-400 text-white placeholder-gray-400 rounded-lg px-4 py-3 min-h-[100px] transition-all duration-200 hover:bg-black/40 resize-none'
																	placeholder='Describe your tribe and its purpose...'
																/>
															</div>
															
															<div className='space-y-2'>
																<Label className='text-sm font-semibold text-purple-300'>
																	Tribe Members
																</Label>
																<SearchableSelect
																	value={
																		editingGroup?.profileIds.join(
																			','
																		) ||
																		''
																	}
																	onValueChange={(
																		value
																	) => {
																		const values =
																			value
																				.split(
																					','
																				)
																				.filter(
																					Boolean
																				);
																		if (
																			editingGroup
																		) {
																			setEditingGroup(
																				{
																					...editingGroup,
																					profileIds:
																						values as Id<'profiles'>[],
																				}
																			);
																		}
																	}}
																	options={
																		availableProfiles?.map(
																			(
																				profile
																			) => {
																				const linkedPlatforms =
																					profile.linkStatuses
																						?.filter(
																							(
																								status
																							) =>
																								status.status ===
																								'linked'
																						)
																						?.map(
																							(
																								status
																							) =>
																								status.platform
																						) ||
																					[];

																				return {
																					value: profile._id,
																					label: profile.profileTitle,
																					render: () => (
																						<div className='flex items-center justify-between w-full'>
																							<div className='flex items-center gap-2'>
																								<span>
																									{
																										profile.profileTitle
																									}
																								</span>
																							</div>
																							<div className='flex gap-1'>
																								{linkedPlatforms.map(
																									(
																										platform
																									) => (
																										<PlatformPill
																											key={
																												platform
																											}
																											platform={
																												platform
																											}
																										/>
																									)
																								)}
																							</div>
																						</div>
																					),
																				};
																			}
																		) ||
																		[]
																	}
																	placeholder='Select profiles to add to your tribe...'
																/>
															</div>
															
															<div className='p-4 bg-gradient-to-r from-green-900/20 to-teal-900/20 rounded-lg border border-green-500/20'>
																<div className='flex items-center space-x-3'>
																	<Checkbox
																		id='editCommunity'
																		checked={editingGroup?.isCommunity || false}
																		onCheckedChange={(checked) => {
																			if (editingGroup) {
																				setEditingGroup({
																					...editingGroup,
																					isCommunity: checked as boolean,
																				});
																			}
																		}}
																		className='border-green-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500'
																	/>
																	<Label htmlFor='editCommunity' className='text-sm font-medium text-green-300 cursor-pointer'>
																		🌍 Share with Watcher Community
																		<p className='text-xs text-gray-400 mt-1'>
																			Allow all community members to access this tribe
																		</p>
																	</Label>
																</div>
															</div>
															
															<div className='text-xs text-gray-400 text-center mb-4'>
																{editingGroup?.profileIds.length || 0} members selected
															</div>
															
															{/* Show selected profiles in edit mode */}
															{editingGroup?.profileIds.length > 0 && (
																<div className='space-y-2 mt-4 max-h-64 overflow-y-auto'>
																	<Label className='text-xs font-semibold text-purple-300'>Current Members:</Label>
																	{editingGroup.profileIds.map((profileId) => {
																		const profile = profiles?.find((p) => p._id === profileId);
																		return (
																			profile && (
																				<div
																					key={profileId}
																					className='flex items-center justify-between p-2 bg-black/20 rounded-lg border border-purple-500/20'
																				>
																					<div className='flex items-center gap-2'>
																						<span className='text-sm font-medium text-white'>
																							{profile.profileTitle}
																						</span>
																						<span className='text-xs text-gray-400'>
																							{profile.email}
																						</span>
																					</div>
																					<div className='flex items-center gap-2'>
																						<div className='flex gap-1'>
																							{profile.linkStatuses
																								?.filter((status) => status.status === 'linked')
																								.map((status) => (
																									<PlatformPill
																										key={status.platform}
																										platform={status.platform}
																									/>
																								))}
																						</div>
																						<Button
																							variant='ghost'
																							size='sm'
																							onClick={() => {
																								setMemberToRemove({
																									profileId,
																									profileName: profile.profileTitle,
																									groupId: group._id,
																								});
																							}}
																							className='h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/20'
																						>
																							❌
																						</Button>
																					</div>
																				</div>
																			)
																		);
																	})}
																</div>
															)}
														</div>
													) : (
														<Card className='p-6 mt-4'>
															<div className='space-y-4'>
																{group.profileIds.map(
																	(
																		profileId: Id<'profiles'>
																	) => {
																		const profile =
																			profiles?.find(
																				(
																					p
																				) =>
																					p._id ===
																					profileId
																			);
																		return (
																			profile && (
																				<div
																					key={
																						profileId
																					}
																					className='flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700'
																				>
																					<div className='flex items-center gap-4'>
																						<div className='flex flex-col'>
																							<span className='font-semibold'>
																								{
																									profile.profileTitle
																								}
																							</span>
																							<span className='text-sm text-gray-400'>
																								{
																									profile.email
																								}
																							</span>
																						</div>
																					</div>
																					<div className='flex gap-1'>
																						{profile.linkStatuses
																							?.filter(
																								(
																									status
																								) =>
																									status.status ===
																									'linked'
																							)
																							.map(
																								(
																									status
																								) => (
																									<PlatformPill
																										key={
																											status.platform
																										}
																										platform={
																											status.platform
																										}
																									/>
																								)
																							)}
																					</div>
																				</div>
																			)
																		);
																	}
																)}
															</div>
														</Card>
													)}
												</motion.div>
											)}
										</AnimatePresence>
										</div>
									</div>
								))
						)}
					</div>
				</CardContent>
			</Card>

			<Card className='mb-6 mt-16 glow-card'>
				<CardHeader>
					<CardTitle className='pink-title'>
						Create New Tribe
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className='space-y-6 p-6 bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-xl border border-purple-500/20'>
						<div className='space-y-2'>
							<Label className='text-sm font-semibold text-purple-300'>
								Tribe Name
							</Label>
							<Input
								placeholder='Enter a memorable tribe name...'
								value={newGroup.name}
								onChange={(e) =>
									setNewGroup({
										...newGroup,
										name: e.target.value,
									})
								}
								className='w-full bg-black/30 border-purple-500/30 focus:border-purple-400 text-white placeholder-gray-400 rounded-lg px-4 py-3 transition-all duration-200 hover:bg-black/40'
							/>
						</div>
						
						<div className='space-y-2'>
							<Label className='text-sm font-semibold text-purple-300'>
								Description
							</Label>
							<Textarea
								placeholder='Describe your tribe and its purpose...'
								value={newGroup.description}
								onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
									setNewGroup({
										...newGroup,
										description: e.target.value,
									})
								}
								className='w-full bg-black/30 border-purple-500/30 focus:border-purple-400 text-white placeholder-gray-400 rounded-lg px-4 py-3 min-h-[100px] transition-all duration-200 hover:bg-black/40 resize-none'
							/>
						</div>
						
						<div className='space-y-2'>
							<Label className='text-sm font-semibold text-purple-300'>
								Tribe Members
							</Label>
							<SearchableSelect
								value={newGroup.profileIds.join(',')}
								onValueChange={(value) => {
									const values = value.split(',').filter(Boolean);
									setNewGroup({
										...newGroup,
										profileIds: values as Id<'profiles'>[],
									});
								}}
								options={
									availableProfiles?.map((profile) => {
										const linkedPlatforms =
											profile.linkStatuses
												?.filter(
													(status) =>
														status.status === 'linked'
												)
												?.map(
													(status) => status.platform
												) || [];

										return {
											value: profile._id,
											label: profile.profileTitle,
											render: () => (
												<div className='flex items-center justify-between w-full gap-2'>
													<div className='flex items-center gap-2'>
														<span>
															{profile.profileTitle}
														</span>
													</div>
													<div className='flex gap-1'>
														{linkedPlatforms.map(
															(platform) => (
																<PlatformPill
																	key={platform}
																	platform={
																		platform
																	}
																/>
															)
														)}
													</div>
												</div>
											),
										};
									}) || []
								}
								placeholder='Select profiles to add to your tribe...'
							/>
						</div>
						
						<div className='p-4 bg-gradient-to-r from-green-900/20 to-teal-900/20 rounded-lg border border-green-500/20'>
							<div className='flex items-center space-x-3'>
								<Checkbox
									id='isCommunity'
									checked={newGroup.isCommunity}
									onCheckedChange={(checked) =>
										setNewGroup({
											...newGroup,
											isCommunity: checked as boolean,
										})
									}
									className='border-green-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500'
								/>
								<Label htmlFor='isCommunity' className='text-sm font-medium text-green-300 cursor-pointer'>
									🌍 Share with Watcher Community
									<p className='text-xs text-gray-400 mt-1'>
										Allow all community members to access this tribe
									</p>
								</Label>
							</div>
						</div>
						
						<div className='text-xs text-gray-400 text-center'>
							{newGroup.profileIds.length} members selected
						</div>
						
						<Button
							onClick={handleCreateGroup}
							disabled={
								!newGroup.name || newGroup.profileIds.length === 0
							}
							className='w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-lg rounded-full'
						>
							<span className='inline-flex items-center gap-2'>
								<svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
								</svg>
								Create Tribe
							</span>
						</Button>
					</div>
				</CardContent>
			</Card>

			{isSuperAdmin && <DefaultGroupCard />}

			<ConfirmDialog
				isOpen={groupToDelete !== null}
				onClose={() => setGroupToDelete(null)}
				title={
					<div className='text-2xl font-bold text-red-600 flex items-center gap-2'>
						🔥 Delete Group 🔥
					</div>
				}
				description={
					<div className='text-lg text-red-500 font-semibold mt-2 text-center'>
						⚠️ Warning: This action cannot be undone! ⚠️
						<p className='text-base text-gray-600 mt-2'>
							Are you sure you want to permanently delete this
							group?
						</p>
					</div>
				}
				onConfirm={async () => {
					if (groupToDelete) {
						try {
							await removeGroup({
								groupId: groupToDelete as Id<'profileGroups'>,
							});
							setGroupToDelete(null);
							toast({
								title: '✨ Group Deleted ✨',
								description:
									'Profile group deleted successfully',
								duration: 3000,
							});
						} catch (error) {
							toast({
								title: 'Error',
								description:
									error instanceof Error
										? error.message
										: 'Failed to delete group',
								variant: 'destructive',
								duration: 3000,
							});
						}
					}
				}}
			/>

			<ConfirmDialog
				isOpen={memberToRemove !== null}
				onClose={() => setMemberToRemove(null)}
				title={
					<div className='text-xl font-bold text-orange-600 flex items-center gap-2'>
						🚫 Remove Member
					</div>
				}
				description={
					<div className='text-base text-gray-600 mt-2 text-center'>
						Are you sure you want to remove <span className='font-bold text-orange-500'>{memberToRemove?.profileName}</span> from this tribe?
					</div>
				}
				onConfirm={() => {
					if (memberToRemove && editingGroup) {
						const updatedProfileIds = editingGroup.profileIds.filter(
							(id) => id !== memberToRemove.profileId
						);
						setEditingGroup({
							...editingGroup,
							profileIds: updatedProfileIds,
						});
						setMemberToRemove(null);
						toast({
							title: '✨ Member Removed',
							description: `${memberToRemove.profileName} has been removed from the tribe`,
							duration: 3000,
						});
					}
				}}
			/>
		</div>
	);
}
